import type { ContextLink } from "@/components/ContextLinkBlock";

/**
 * Internal-linking map between salon pages and their local service pages.
 * Only paths that exist as routes are listed here.
 */

/** Salon page -> local service pages for that salon. */
export const SALON_SERVICE_LINKS: Record<string, { city: string; links: ContextLink[] }> = {
  taby: {
    city: "Täby Centrum",
    links: [
      {
        href: "/herrklippning-taby/",
        label: "Herrklippning i Täby Centrum",
        blurb: "Klippning med maskin, sax och rakkniv i Westfield Täby Centrum.",
      },
      {
        href: "/skin-fade-taby/",
        label: "Skin fade i Täby Centrum",
        blurb: "Skarp övergång ner till huden, formad efter huvudform och hårväxt.",
      },
      {
        href: "/barnklippning-taby/",
        label: "Barnklippning i Täby Centrum",
        blurb: "Lugn klippning för barn, i barnets tempo.",
      },
    ],
  },
  arninge: {
    city: "Arninge",
    links: [
      {
        href: "/herrklippning-arninge/",
        label: "Herrklippning i Arninge",
        blurb: "Herrklippning i ICA Maxi Huset med gratis parkering utanför.",
      },
      {
        href: "/skin-fade-arninge/",
        label: "Skin fade i Arninge",
        blurb: "Fade ner till huden av barberare som gör det varje dag.",
      },
      {
        href: "/barnklippning-arninge/",
        label: "Barnklippning i Arninge",
        blurb: "Klippning för barn, enkelt att kombinera med handlingen.",
      },
    ],
  },
  akersberga: {
    city: "Åkersberga",
    links: [
      {
        href: "/herrklippning-akersberga/",
        label: "Herrklippning i Åkersberga",
        blurb: "Herrklippning inne i ICA Maxi, drop-in i mån av tid.",
      },
      {
        href: "/skin-fade-akersberga/",
        label: "Skin fade i Åkersberga",
        blurb: "Low, mid och high skin fade med ren nacklinje.",
      },
      {
        href: "/barnklippning-akersberga/",
        label: "Barnklippning i Åkersberga",
        blurb: "Barnklippning med gratis parkering direkt utanför.",
      },
    ],
  },
  "ahlens-city": {
    city: "Stockholm City",
    links: [
      {
        href: "/herrklippning/",
        label: "Herrklippning i Stockholm City",
        blurb: "Klippning 450 kr i Åhléns, en minut från T-Centralen.",
      },
      {
        href: "/skin-fade/",
        label: "Skin fade i Stockholm City",
        blurb: "Fade ner till huden, mitt i city på Klarabergsgatan.",
      },
      {
        href: "/skaggtrimning/",
        label: "Skäggtrimning i Stockholm City",
        blurb: "Form, konturer och rakkniv, 380 kr i Åhléns City.",
      },
    ],
  },
};

/** General service page -> the same service on each salon. */
export const SERVICE_SALON_LINKS: Record<
  string,
  { heading: string; intro: string; links: ContextLink[] }
> = {
  "herrklippning-stockholm": {
    heading: "Boka herrklippning på din närmaste salong",
    intro: "Samma pris och samma hantverk på alla fyra salonger. Välj den som ligger närmast dig.",
    links: [
      { href: "/herrklippning-taby/", label: "Herrklippning i Täby Centrum" },
      { href: "/barberare-stockholm-ahlens-city/", label: "Herrklippning på Tailor's Barber Åhléns City" },
      { href: "/herrklippning-akersberga/", label: "Herrklippning i Åkersberga" },
      { href: "/herrklippning-arninge/", label: "Herrklippning i Arninge" },
    ],
  },
  "skin-fade": {
    heading: "Boka skin fade på din närmaste salong",
    intro:
      "Skin fade finns på alla våra salonger. Välj ort och boka hos en barberare som gör fade dagligen.",
    links: [
      { href: "/skin-fade-taby/", label: "Skin fade i Täby Centrum" },
      { href: "/barberare-stockholm-ahlens-city/", label: "Skin fade på Tailor's Barber Åhléns City" },
      { href: "/skin-fade-akersberga/", label: "Skin fade i Åkersberga" },
      { href: "/skin-fade-arninge/", label: "Skin fade i Arninge" },
    ],
  },
  barnklippning: {
    heading: "Boka barnklippning på din närmaste salong",
    intro: "Barnklippning finns i Täby Centrum, Åkersberga och Arninge.",
    links: [
      { href: "/barnklippning-taby/", label: "Barnklippning i Täby Centrum" },
      { href: "/barnklippning-akersberga/", label: "Barnklippning i Åkersberga" },
      { href: "/barnklippning-arninge/", label: "Barnklippning i Arninge" },
    ],
  },
  skaggtrimning: {
    heading: "Boka skäggtrimning på din närmaste salong",
    intro: "Skäggtrimning kostar 380 kr på alla fyra salonger. Välj ort och boka tid.",
    links: [
      { href: "/barberare-stockholm-ahlens-city/", label: "Skäggtrimning på Tailor's Barber Åhléns City" },
      { href: "/barberare-stockholm-taby/", label: "Skäggtrimning i Täby Centrum" },
      { href: "/barberare-stockholm-akersberga/", label: "Skäggtrimning i Åkersberga" },
      { href: "/barberare-stockholm-arninge/", label: "Skäggtrimning i Arninge" },
    ],
  },
};

/** Local service page -> its salon page and the other two services in the same town. */
const LOCAL_TOWNS: { key: string; town: string; label: string }[] = [
  { key: "taby", town: "Täby", label: "Täby Centrum" },
  { key: "arninge", town: "Arninge", label: "Arninge" },
  { key: "akersberga", town: "Åkersberga", label: "Åkersberga" },
];

export const LOCAL_SERVICE_MORE: Record<string, { heading: string; links: ContextLink[] }> =
  Object.fromEntries(
    LOCAL_TOWNS.flatMap(({ key, town, label }) =>
      ["herrklippning", "skin-fade", "barnklippning"].map((svc) => {
        const slug = `${svc}-${key}`;
        const salon: ContextLink = {
          href: `/barberare-stockholm-${key}/`,
          label: `Tailor's Barber ${label}`,
        };
        const others = SALON_SERVICE_LINKS[key]!.links.filter((l) => l.href !== `/${slug}/`);
        return [slug, { heading: `Mer i ${town}`, links: [salon, ...others] }];
      }),
    ),
  );
